import { useMemo } from 'react'
import { useDebounce } from '@vtex/admin-ui'

export type ProductSearchByOptions = keyof typeof comboboxItemIDs

const comboboxItemIDs = {
  ean: 'ean',
  productName: 'productName',
  skuName: 'skuName',
  productRefId: 'productRefId',
  skuRefId: 'skuRefId',
  productId: 'productId',
  skuId: 'skuId',
} as const

export const numericSearchIds = [
  comboboxItemIDs.productId,
  comboboxItemIDs.skuId,
]

const defaultComboboxItemIds = [
  comboboxItemIDs.skuRefId,
  comboboxItemIDs.productRefId,
  comboboxItemIDs.skuId,
  comboboxItemIDs.productName,
  comboboxItemIDs.ean,
  comboboxItemIDs.productId,
  comboboxItemIDs.skuName,
] as const

const startWithLetterComboboxItemIds = [
  comboboxItemIDs.skuRefId,
  comboboxItemIDs.productRefId,
  comboboxItemIDs.productName,
  comboboxItemIDs.ean,
  comboboxItemIDs.skuName,
] as const

const startWithNumberComboboxItemIds = [
  comboboxItemIDs.skuRefId,
  comboboxItemIDs.productRefId,
  comboboxItemIDs.skuId,
  comboboxItemIDs.ean,
  comboboxItemIDs.productId,
  comboboxItemIDs.productName,
  comboboxItemIDs.skuName,
] as const

type OptionsToReturn =
  | typeof defaultComboboxItemIds
  | typeof startWithLetterComboboxItemIds
  | typeof startWithNumberComboboxItemIds

const digitRegex = new RegExp(/\d/)

const isNumber = (value?: string) => !!value && digitRegex.exec(value[0])

type UseSearchOptions = {
  value: string
  selectedId?: string
  pinnedSearchBy?: ProductSearchByOptions | ''
}

export const useSearchOptionsByValue = ({
  value,
  pinnedSearchBy = '',
}: UseSearchOptions) => {
  const [debouncedValue] = useDebounce(value, 300)

  return useMemo(() => {
    let options: OptionsToReturn = defaultComboboxItemIds

    if (debouncedValue.length <= 10) {
      options = isNumber(debouncedValue.toString())
        ? startWithNumberComboboxItemIds
        : startWithLetterComboboxItemIds
    } else {
      options = startWithLetterComboboxItemIds
    }

    if (!!pinnedSearchBy) {
      const pinnedOption = options.find((option) => option === pinnedSearchBy)

      if (pinnedOption) {
        const otherOptions = options.filter(
          (option) => option !== pinnedSearchBy
        )

        return [pinnedOption, ...otherOptions]
      }

      return [pinnedSearchBy, ...options]
    }

    return options
  }, [debouncedValue, pinnedSearchBy])
}
